import { inject } from '@angular/core';
import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
} from '@ngrx/signals';
import { computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ExpertApiService } from '../services/expert-api.service';
import {
  ExpertDTO,
  CreateExpertDTO,
  UpdateExpertDTO,
} from '../types/expert.types';
import { DashboardStatsDTO } from '../types/stats.types';
import { TApiErrorResponse } from '@sandbox-platform/shared-ui';

// Expert state interface
interface ExpertState {
  selectedExpert: ExpertDTO | null;
  isLoading: boolean;
  error: string | TApiErrorResponse | null;
  stats: {
    dashboardStats: DashboardStatsDTO | null;
    loading: boolean;
    error: string | TApiErrorResponse | null;
  };
}

// Initial state
const initialState: ExpertState = {
  selectedExpert: null,
  isLoading: false,
  error: null,
  stats: {
    dashboardStats: null,
    loading: false,
    error: null,
  },
};

export const ExpertStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withComputed((store) => ({
    // Add any expert-specific computed properties here if needed
  })),

  withMethods((store) => {
    const expertApiService = inject(ExpertApiService);

    return {
      setLoading: (loading: boolean) => {
        patchState(store, { isLoading: loading });
      },

      setError: (error: string | null) => {
        patchState(store, { error });
      },

      clearError: () => {
        patchState(store, { error: null });
      },

      setSelectedExpert: (expert: ExpertDTO | null) => {
        patchState(store, { selectedExpert: expert });
      },

      /**
       * Load expert by ID
       */
      loadExpertById: async (expertId: string): Promise<void> => {
        patchState(store, { isLoading: true, error: null });

        try {
          const response = await firstValueFrom(
            expertApiService.getById(expertId)
          );

          if (response?.data) {
            patchState(store, {
              selectedExpert: response.data,
              error: null,
            });
          }
        } catch (error) {
          patchState(store, {
            error:
              error instanceof Error ? error.message : 'Failed to load expert',
          });
        } finally {
          patchState(store, { isLoading: false });
        }
      },

      /**
       * Create a new expert
       */
      createExpert: async (expertData: CreateExpertDTO): Promise<boolean> => {
        patchState(store, { isLoading: true, error: null });

        try {
          const response = await firstValueFrom(
            expertApiService.create(expertData)
          );

          if (response?.data) {
            patchState(store, {
              selectedExpert: response.data,
              error: null,
            });

            return true;
          }
          return false;
        } catch (error) {
          patchState(store, {
            error:
              error instanceof Error
                ? error.message
                : 'Failed to create expert',
          });
          return false;
        } finally {
          patchState(store, { isLoading: false });
        }
      },

      /**
       * Update an expert
       */
      updateExpert: async (
        expertId: string,
        expertData: UpdateExpertDTO
      ): Promise<boolean> => {
        patchState(store, { isLoading: true, error: null });

        try {
          const response = await firstValueFrom(
            expertApiService.update(expertId, expertData)
          );

          if (response?.data) {
            patchState(store, {
              selectedExpert: response.data,
              error: null,
            });

            return true;
          }
          return false;
        } catch (error) {
          patchState(store, {
            error:
              error instanceof Error
                ? error.message
                : 'Failed to update expert',
          });
          return false;
        } finally {
          patchState(store, { isLoading: false });
        }
      },

      /**
       * Load expert statistics
       */
      loadExpertStats: async (): Promise<void> => {
        patchState(store, {
          stats: { ...store.stats(), loading: true, error: null },
        });

        try {
          const response = await firstValueFrom(
            expertApiService.getDashboardStats()
          );

          if (response?.data) {
            patchState(store, {
              stats: {
                dashboardStats: response.data,
                loading: false,
                error: null,
              },
            });
          }
        } catch (error) {
          patchState(store, {
            stats: {
              ...store.stats(),
              loading: false,
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to load expert statistics',
            },
          });
        }
      },
    };
  })
);

// Create a type for the store instance
export type ExpertStoreType = InstanceType<typeof ExpertStore>;
